import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";

interface SelfServiceSettings {
  allow_cancel: boolean;
  allow_rebook: boolean;
  cancel_deadline_hours: number;
  rebook_deadline_hours: number;
  show_in_shop: boolean;
  show_in_email: boolean;
  show_on_thank_you: boolean;
  allowed_statuses: string[];
  rebook_notify_customer: boolean;
  rebook_notify_organisation: boolean;
  cancel_notify_customer: boolean;
  cancel_notify_organisation: boolean;
}

const ALL_STATUSES = [
  { value: "pending", labelKey: "reservations.status_pending" },
  { value: "confirmed", labelKey: "reservations.status_confirmed" },
];

const DEFAULT_SETTINGS: SelfServiceSettings = {
  allow_cancel: true,
  allow_rebook: true,
  cancel_deadline_hours: 24,
  rebook_deadline_hours: 24,
  show_in_shop: true,
  show_in_email: true,
  show_on_thank_you: true,
  allowed_statuses: ["pending", "confirmed"],
  rebook_notify_customer: true,
  rebook_notify_organisation: true,
  cancel_notify_customer: true,
  cancel_notify_organisation: true,
};

const SelfServiceExtensionSettings = () => {
  const { t } = useLanguage();
  const { profile } = useAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState<SelfServiceSettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [extensionId, setExtensionId] = useState<string | null>(null);

  const orgId = profile?.organisation_id;

  useEffect(() => {
    if (!orgId) return;
    const load = async () => {
      const { data } = await supabase
        .from("organisation_extensions")
        .select("*")
        .eq("organisation_id", orgId)
        .eq("extension_id", "self-service")
        .maybeSingle();
      if (data) {
        setExtensionId((data as any).id);
        const s = (data as any).settings as Partial<SelfServiceSettings>;
        setSettings({ ...DEFAULT_SETTINGS, ...s });
      }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const save = async () => {
    if (!orgId) return;
    setSaving(true);
    if (extensionId) {
      await supabase
        .from("organisation_extensions")
        .update({ settings: settings as any })
        .eq("id", extensionId);
    } else {
      const { data } = await supabase
        .from("organisation_extensions")
        .insert({
          organisation_id: orgId,
          extension_id: "self-service",
          enabled: true,
          settings: settings as any,
        })
        .select()
        .single();
      if (data) setExtensionId((data as any).id);
    }
    setSaving(false);
    toast({ title: t("ext.ss_saved") });
  };

  const toggleStatus = (status: string) => {
    setSettings((s) => {
      const current = s.allowed_statuses || [];
      if (current.includes(status)) {
        return { ...s, allowed_statuses: current.filter((st) => st !== status) };
      } else {
        return { ...s, allowed_statuses: [...current, status] };
      }
    });
  };

  if (loading) return <div className="text-sm text-muted-foreground">{t("ext.loading")}</div>;

  return (
    <div>
      <h1 className="text-xl font-bold text-foreground mb-1">{t("ext.ss_title")}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t("ext.ss_subtitle")}</p>

      <div className="space-y-6 max-w-lg">
        {/* Allowed statuses */}
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-card-foreground mb-1">{t("ext.ss_allowed_statuses")}</h3>
          <p className="text-xs text-muted-foreground mb-3">{t("ext.ss_allowed_statuses_desc")}</p>
          <div className="space-y-2">
            {ALL_STATUSES.map((s) => (
              <label key={s.value} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={(settings.allowed_statuses || []).includes(s.value)}
                  onCheckedChange={() => toggleStatus(s.value)}
                />
                <span className="text-sm text-card-foreground">{t(s.labelKey)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Cancellation */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-card-foreground">{t("ext.ss_allow_cancel")}</h3>
              <p className="text-xs text-muted-foreground">{t("ext.ss_allow_cancel_desc")}</p>
            </div>
            <Switch checked={settings.allow_cancel} onCheckedChange={(v) => setSettings((s) => ({ ...s, allow_cancel: v }))} />
          </div>
          {settings.allow_cancel && (
            <div className="space-y-2">
              <Label className="text-xs">{t("ext.ss_cancel_deadline")}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  value={settings.cancel_deadline_hours}
                  onChange={(e) => setSettings((s) => ({ ...s, cancel_deadline_hours: parseInt(e.target.value) || 0 }))}
                  className="w-24"
                />
                <span className="text-xs text-muted-foreground">{t("ext.ss_hours_before")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Cancel Notifications */}
        {settings.allow_cancel && (
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <h3 className="text-sm font-semibold text-card-foreground">{t("ext.ss_cancel_notifications")}</h3>
            <p className="text-xs text-muted-foreground">{t("ext.ss_cancel_notifications_desc")}</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-card-foreground">{t("ext.ss_cancel_notify_customer")}</p>
                <p className="text-xs text-muted-foreground">{t("ext.ss_cancel_notify_customer_desc")}</p>
              </div>
              <Switch checked={settings.cancel_notify_customer} onCheckedChange={(v) => setSettings((s) => ({ ...s, cancel_notify_customer: v }))} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-card-foreground">{t("ext.ss_cancel_notify_organisation")}</p>
                <p className="text-xs text-muted-foreground">{t("ext.ss_cancel_notify_organisation_desc")}</p>
              </div>
              <Switch checked={settings.cancel_notify_organisation} onCheckedChange={(v) => setSettings((s) => ({ ...s, cancel_notify_organisation: v }))} />
            </div>
          </div>
        )}

        {/* Rebooking */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-card-foreground">{t("ext.ss_allow_rebook")}</h3>
              <p className="text-xs text-muted-foreground">{t("ext.ss_allow_rebook_desc")}</p>
            </div>
            <Switch checked={settings.allow_rebook} onCheckedChange={(v) => setSettings((s) => ({ ...s, allow_rebook: v }))} />
          </div>
          {settings.allow_rebook && (
            <div className="space-y-2">
              <Label className="text-xs">{t("ext.ss_rebook_deadline")}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  value={settings.rebook_deadline_hours}
                  onChange={(e) => setSettings((s) => ({ ...s, rebook_deadline_hours: parseInt(e.target.value) || 0 }))}
                  className="w-24"
                />
                <span className="text-xs text-muted-foreground">{t("ext.ss_hours_before")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Rebook Notifications */}
        {settings.allow_rebook && (
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <h3 className="text-sm font-semibold text-card-foreground">{t("ext.ss_rebook_notifications")}</h3>
            <p className="text-xs text-muted-foreground">{t("ext.ss_rebook_notifications_desc")}</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-card-foreground">{t("ext.ss_notify_customer")}</p>
                <p className="text-xs text-muted-foreground">{t("ext.ss_notify_customer_desc")}</p>
              </div>
              <Switch checked={settings.rebook_notify_customer} onCheckedChange={(v) => setSettings((s) => ({ ...s, rebook_notify_customer: v }))} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-card-foreground">{t("ext.ss_notify_organisation")}</p>
                <p className="text-xs text-muted-foreground">{t("ext.ss_notify_organisation_desc")}</p>
              </div>
              <Switch checked={settings.rebook_notify_organisation} onCheckedChange={(v) => setSettings((s) => ({ ...s, rebook_notify_organisation: v }))} />
            </div>
          </div>
        )}

        <Separator />

        {/* Visibility */}
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <h3 className="text-sm font-semibold text-card-foreground">{t("ext.ss_visibility")}</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-card-foreground">{t("ext.ss_show_shop")}</p>
              <p className="text-xs text-muted-foreground">{t("ext.ss_show_shop_desc")}</p>
            </div>
            <Switch checked={settings.show_in_shop} onCheckedChange={(v) => setSettings((s) => ({ ...s, show_in_shop: v }))} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-card-foreground">{t("ext.ss_show_email")}</p>
              <p className="text-xs text-muted-foreground">{t("ext.ss_show_email_desc")}</p>
            </div>
            <Switch checked={settings.show_in_email} onCheckedChange={(v) => setSettings((s) => ({ ...s, show_in_email: v }))} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-card-foreground">{t("ext.ss_show_thank_you")}</p>
              <p className="text-xs text-muted-foreground">{t("ext.ss_show_thank_you_desc")}</p>
            </div>
            <Switch checked={settings.show_on_thank_you} onCheckedChange={(v) => setSettings((s) => ({ ...s, show_on_thank_you: v }))} />
          </div>
        </div>

        <Button onClick={save} disabled={saving}>
          {saving ? t("ext.ss_saving") : t("ext.ss_save")}
        </Button>
      </div>
    </div>
  );
};

export default SelfServiceExtensionSettings;
